(function () {
  // Fallback country ISO code -> [min, max] business days, used only if the
  // "Delivery locations" section (#klydo-delivery-days) isn't present.
  const DEFAULT_DELIVERY_DAYS = {
    US: [3, 6],
    GB: [2, 4],
    DE: [2, 4],
    FR: [2, 4],
    IT: [2, 4],
    NL: [2, 4],
    AE: [5, 9],
    CN: [2, 3],
  };

  // Prefer the merchant-editable map emitted by the Delivery locations section;
  // keys are normalized to uppercase so matching is case-insensitive. An empty
  // map is honoured rather than replaced by the fallback: clearing the setting
  // is how a merchant switches the estimate off, so only a missing element
  // (snippet not rendered at all) falls back to the hardcoded days.
  function loadDeliveryDays() {
    const element = document.getElementById("klydo-delivery-days");
    if (!element) return DEFAULT_DELIVERY_DAYS;
    try {
      const parsed = JSON.parse(element.textContent);
      const map = {};
      Object.keys(parsed).forEach((code) => {
        map[code.toUpperCase()] = parsed[code];
      });
      return map;
    } catch (error) {
      return DEFAULT_DELIVERY_DAYS;
    }
  }

  const DELIVERY_DAYS = loadDeliveryDays();

  // Merchant-listed closure dates as ["<country or ALL>", "YYYY-MM-DD"] pairs,
  // emitted by the snippet from the "Delivery holidays" theme setting.
  function loadHolidayPairs() {
    const element = document.getElementById("klydo-delivery-holidays");
    if (!element) return [];
    try {
      const parsed = JSON.parse(element.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  const HOLIDAY_PAIRS = loadHolidayPairs();

  // The closures that apply to one country: its own plus the global ALL rows.
  function holidaysFor(country) {
    const code = country.toUpperCase();
    const dates = new Set();
    HOLIDAY_PAIRS.forEach((pair) => {
      if (!Array.isArray(pair) || pair.length < 2) return;
      const scope = String(pair[0]).toUpperCase();
      if (scope === "ALL" || scope === code) dates.add(String(pair[1]));
    });
    return dates;
  }

  // Format in the storefront's active language so month names match the page.
  const locale = document.documentElement.lang || undefined;
  const formatDate = (date) =>
    date.toLocaleDateString(locale, { month: "short", day: "numeric" });

  // The fulfilment timezone the business-day clock runs in. Without it the
  // whole window rides on the visitor's own clock: someone browsing Monday
  // morning in UTC+11 is still Sunday at the warehouse, which would start the
  // count a business day early and match holiday dates on the wrong day.
  function loadOriginTimeZone() {
    const element = document.getElementById("klydo-delivery-timezone");
    if (!element) return null;
    try {
      const zone = JSON.parse(element.textContent);
      if (!zone) return null;
      // Reject a typo'd zone here rather than throwing on every format call.
      new Intl.DateTimeFormat("en-CA", { timeZone: zone });
      return zone;
    } catch (error) {
      return null;
    }
  }

  const ORIGIN_TIME_ZONE = loadOriginTimeZone();

  // Today at the fulfilment origin, as a local Date carrying that calendar
  // day's Y/M/D. Every later getDay()/getDate() call is then stable regardless
  // of where the visitor is. Falls back to the visitor's own date if no usable
  // zone is configured.
  function originToday() {
    if (!ORIGIN_TIME_ZONE) return new Date();
    const formatted = new Intl.DateTimeFormat("en-CA", {
      timeZone: ORIGIN_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
    const [year, month, day] = formatted.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

  // Local-time YYYY-MM-DD. Built by hand rather than via toISOString(), which
  // would shift the date into UTC and mismatch the merchant's calendar dates.
  function toISODate(date) {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
  }

  const isClosed = (date, holidays) =>
    isWeekend(date) || holidays.has(toISODate(date));

  // A run of closures long enough to hit this means the setting is nonsense;
  // bail out rather than spin the loop forever on merchant free text.
  const MAX_DAYS_TO_WALK = 400;

  // Orders placed while the warehouse is closed aren't handled until it opens,
  // so the estimate counts from there rather than from the browsing date.
  function nextBusinessDay(startDate, holidays) {
    const resultDate = new Date(startDate);
    let walkedDays = 0;
    while (isClosed(resultDate, holidays) && walkedDays < MAX_DAYS_TO_WALK) {
      resultDate.setDate(resultDate.getDate() + 1);
      walkedDays += 1;
    }
    return resultDate;
  }

  // Add business days, skipping weekends and holidays so closed days never
  // count toward the delivery estimate.
  function addBusinessDays(startDate, businessDaysToAdd, holidays) {
    const resultDate = new Date(startDate);
    let addedDays = 0;
    let walkedDays = 0;
    while (addedDays < businessDaysToAdd && walkedDays < MAX_DAYS_TO_WALK) {
      resultDate.setDate(resultDate.getDate() + 1);
      walkedDays += 1;
      if (!isClosed(resultDate, holidays)) addedDays += 1;
    }
    return resultDate;
  }

  function apply(country) {
    const element = document.querySelector(".product__delivery-estimate");
    if (!element) return;

    const days = country ? DELIVERY_DAYS[country.toUpperCase()] : null;
    if (!days) {
      element.hidden = true;
      return;
    }

    // Tolerate a misconfigured row where min > max so the range never renders
    // backwards.
    const minDays = Math.min(days[0], days[1]);
    const maxDays = Math.max(days[0], days[1]);

    const holidays = holidaysFor(country);
    const orderDate = nextBusinessDay(originToday(), holidays);
    const minDate = addBusinessDays(orderDate, minDays, holidays);
    const maxDate = addBusinessDays(minDate, maxDays - minDays, holidays);

    const dates = element.querySelector(".product__delivery-estimate__dates");
    if (dates)
      dates.textContent = `${formatDate(minDate)} – ${formatDate(maxDate)}`;
    element.hidden = false;
  }

  function init() {
    // Fires now if the country is already known, otherwise when geo resolves.
    window.klydoOnCountry(apply);
  }

  if (typeof window.klydoOnCountry !== "function") return;
  if (document.readyState !== "loading") init();
  else document.addEventListener("DOMContentLoaded", init);
})();
